/**
 * Secure localStorage wrapper using AES-GCM encryption via Web Crypto API.
 * Encrypts sensitive data (auth tokens, user profiles, cart/payment data)
 * while leaving non-sensitive keys (language, splash seen, site password) unencrypted.
 */

const ENCRYPTION_KEY_NAME = 'lavvissa_ek';
const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;

// Derive a stable encryption key from a device fingerprint
async function getOrCreateKey() {
  try {
    const stored = sessionStorage.getItem(ENCRYPTION_KEY_NAME);
    if (stored) {
      const keyData = Uint8Array.from(atob(stored), c => c.charCodeAt(0));
      return await crypto.subtle.importKey(
        'raw',
        keyData,
        { name: ALGORITHM, length: KEY_LENGTH },
        false,
        ['encrypt', 'decrypt']
      );
    }

    const key = await crypto.subtle.generateKey(
      { name: ALGORITHM, length: KEY_LENGTH },
      true,
      ['encrypt', 'decrypt']
    );

    const exported = await crypto.subtle.exportKey('raw', key);
    sessionStorage.setItem(
      ENCRYPTION_KEY_NAME,
      btoa(String.fromCharCode(...new Uint8Array(exported)))
    );

    return key;
  } catch {
    // Fallback: if Web Crypto is unavailable, return null (will use plain storage)
    return null;
  }
}

async function encrypt(plaintext) {
  const key = await getOrCreateKey();
  if (!key) return JSON.stringify({ __plain: true, data: plaintext });

  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = encoder.encode(plaintext);

  const ciphertext = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv },
    key,
    encoded
  );

  return JSON.stringify({
    iv: btoa(String.fromCharCode(...iv)),
    ct: btoa(String.fromCharCode(...new Uint8Array(ciphertext))),
  });
}

async function decrypt(cipherJson) {
  try {
    const parsed = JSON.parse(cipherJson);

    // Handle plain fallback data
    if (parsed.__plain) return parsed.data;

    const key = await getOrCreateKey();
    if (!key) return null;

    const iv = Uint8Array.from(atob(parsed.iv), c => c.charCodeAt(0));
    const ct = Uint8Array.from(atob(parsed.ct), c => c.charCodeAt(0));

    const decrypted = await crypto.subtle.decrypt(
      { name: ALGORITHM, iv },
      key,
      ct
    );

    return new TextDecoder().decode(decrypted);
  } catch {
    // If decryption fails (e.g. key rotated), return null so callers use defaults
    return null;
  }
}

/**
 * Store a value with encryption.
 * @param {string} key - localStorage key
 * @param {string} value - value to store (will be encrypted)
 */
export async function secureSetItem(key, value) {
  try {
    const encrypted = await encrypt(value);
    localStorage.setItem(key, encrypted);
  } catch {
    // Last resort fallback: store as-is
    localStorage.setItem(key, value);
  }
}

/**
 * Retrieve and decrypt a value from localStorage.
 * @param {string} key - localStorage key
 * @returns {Promise<string|null>} decrypted value or null
 */
export async function secureGetItem(key) {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    // Try to decrypt; if the value is not encrypted JSON, return raw
    const decrypted = await decrypt(stored);
    return decrypted;
  } catch {
    // If stored value isn't encrypted (legacy data), return it raw
    return localStorage.getItem(key);
  }
}

/**
 * Remove an item from localStorage.
 * @param {string} key - localStorage key
 */
export function secureRemoveItem(key) {
  localStorage.removeItem(key);
}
